import { useCallback, useEffect, useRef, useState } from "react";
import { Maximize, Minus, Plus } from "lucide-react";

type View = { scale: number; x: number; y: number };

const MAX_ZOOM_FACTOR = 12; // względem skali "dopasuj"
const MIN_ZOOM_FACTOR = 0.5;

/**
 * Podgląd dużego obrazka (legenda planu, arkusz) z powiększaniem i
 * przesuwaniem: kółko myszy (wokół kursora), przeciąganie, dwa palce
 * (pinch) na dotyku, dwuklik = przybliż, przyciski +/−/Dopasuj oraz
 * klawisze + − 0. Obraz startuje dopasowany do okna.
 */
export default function ZoomPanImage({
  src,
  alt,
  className,
  style,
}: {
  src: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const [view, setView] = useState<View>({ scale: 1, x: 0, y: 0 });
  const viewRef = useRef(view);
  viewRef.current = view;
  const fitScaleRef = useRef(1);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef<{ dist: number; midX: number; midY: number } | null>(null);

  const fit = useCallback(() => {
    const el = containerRef.current;
    if (!el || !natural) return;
    const cw = el.clientWidth;
    const ch = el.clientHeight;
    const s = Math.min(cw / natural.w, ch / natural.h);
    fitScaleRef.current = s;
    setView({ scale: s, x: (cw - natural.w * s) / 2, y: (ch - natural.h * s) / 2 });
  }, [natural]);

  useEffect(() => {
    fit();
  }, [fit]);

  // Zmiana rozmiaru okna → ponowne dopasowanie.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => fit());
    ro.observe(el);
    return () => ro.disconnect();
  }, [fit]);

  /** Zoom o `factor` wokół punktu (px, py) w układzie kontenera. */
  const zoomAt = useCallback((factor: number, px: number, py: number) => {
    const v = viewRef.current;
    const fitS = fitScaleRef.current;
    const next = Math.min(fitS * MAX_ZOOM_FACTOR, Math.max(fitS * MIN_ZOOM_FACTOR, v.scale * factor));
    const k = next / v.scale;
    setView({ scale: next, x: px - (px - v.x) * k, y: py - (py - v.y) * k });
  }, []);

  const zoomCenter = useCallback(
    (factor: number) => {
      const el = containerRef.current;
      if (!el) return;
      zoomAt(factor, el.clientWidth / 2, el.clientHeight / 2);
    },
    [zoomAt],
  );

  // Kółko myszy — listener nie-pasywny, żeby zablokować przewijanie strony.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      zoomAt(e.deltaY < 0 ? 1.2 : 1 / 1.2, e.clientX - r.left, e.clientY - r.top);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [zoomAt]);

  // Klawiatura: + − 0
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "+" || e.key === "=") zoomCenter(1.3);
      else if (e.key === "-" || e.key === "_") zoomCenter(1 / 1.3);
      else if (e.key === "0") fit();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoomCenter, fit]);

  const local = (e: React.PointerEvent) => {
    const r = containerRef.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, local(e));
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      gesture.current = {
        dist: Math.hypot(a.x - b.x, a.y - b.y),
        midX: (a.x + b.x) / 2,
        midY: (a.y + b.y) / 2,
      };
    }
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const prev = pointers.current.get(e.pointerId);
    if (!prev) return;
    const cur = local(e);
    pointers.current.set(e.pointerId, cur);
    if (pointers.current.size >= 2 && gesture.current) {
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const midX = (a.x + b.x) / 2;
      const midY = (a.y + b.y) / 2;
      const g = gesture.current;
      if (g.dist > 0) zoomAt(dist / g.dist, midX, midY);
      setView((v) => ({ ...v, x: v.x + (midX - g.midX), y: v.y + (midY - g.midY) }));
      gesture.current = { dist, midX, midY };
      return;
    }
    setView((v) => ({ ...v, x: v.x + (cur.x - prev.x), y: v.y + (cur.y - prev.y) }));
  };

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) gesture.current = null;
  };

  const btn =
    "inline-flex h-9 w-9 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-800 shadow-sm hover:bg-gray-50";

  return (
    <div className={`relative ${className ?? ""}`} style={style}>
      <div
        ref={containerRef}
        className="absolute inset-0 overflow-hidden rounded bg-gray-100 cursor-grab active:cursor-grabbing select-none"
        style={{ touchAction: "none" }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={(e) => {
          const r = containerRef.current!.getBoundingClientRect();
          zoomAt(2, e.clientX - r.left, e.clientY - r.top);
        }}>
        <img
          src={src}
          alt={alt}
          draggable={false}
          onLoad={(e) => setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            maxWidth: "none",
            transformOrigin: "0 0",
            transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
            visibility: natural ? "visible" : "hidden",
          }}
        />
        {!natural && (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-500">Wczytywanie…</div>
        )}
      </div>
      <div className="absolute right-2 top-2 flex flex-col gap-1.5">
        <button type="button" className={btn} onClick={() => zoomCenter(1.3)} title="Powiększ (+)" aria-label="Powiększ">
          <Plus className="h-4 w-4" />
        </button>
        <button type="button" className={btn} onClick={() => zoomCenter(1 / 1.3)} title="Pomniejsz (−)" aria-label="Pomniejsz">
          <Minus className="h-4 w-4" />
        </button>
        <button type="button" className={btn} onClick={fit} title="Dopasuj do okna (0)" aria-label="Dopasuj do okna">
          <Maximize className="h-4 w-4" />
        </button>
      </div>
      <div className="pointer-events-none absolute bottom-2 left-2 rounded bg-white/85 px-2 py-1 text-xs text-gray-600 shadow-sm">
        Kółko myszy lub dwa palce — powiększanie · przeciągnij — przesuwanie · dwuklik — przybliż
      </div>
    </div>
  );
}
